/**
 * Her sekme kendi hesabıyla, Supabase oturumunun saklanma biçimi.
 *
 * Varsayılanda bir tarayıcı tek oturum tutuyor: bir sekmede garsonla girmek
 * ya da çıkmak, patronun açık sekmesini de değiştiriyordu. O sekme ekranda
 * patron görünmeye devam ediyor, sunucuya giden istekler ise garsonun
 * oturumuyla ya da oturumsuz gidiyor ve "Yetkisiz" dönüyordu.
 *
 * Üç kural:
 *
 * 1. Sekme, girdiği hesabı hatırlıyor (sessionStorage). Başka bir sekmede
 *    girilen hesap onu değiştirmiyor.
 *
 * 2. Oturumun kendisi kullanıcıya göre localStorage'da duruyor, sekmeye göre
 *    değil. Aynı hesabın açık olduğu sekmeler tek kopyayı paylaşıyor: biri
 *    anahtarı yenileyince öteki eski yenileme anahtarıyla kalmıyor. Kalsaydı
 *    Supabase eski anahtarın yeniden kullanılmasını ihlal sayıp oturum
 *    ailesinin tamamını kapatırdı.
 *
 * 3. Yeni sekme ya da uygulamanın yeniden açılması son girilen hesapla
 *    başlıyor. Kasa her sabah yeniden giriş istemiyor.
 *
 * auth-js sekmeler arası haberi, oturum anahtarının adıyla açtığı bir
 * BroadcastChannel'dan veriyor. Anahtar adı sekmeye özgü (bkz. tabStorageKey), yani
 * bir sekmedeki giriş öteki sekmeye "şu kullanıcı girdi" diye yayılmıyor.
 */

const TAB_ID = 'adissoo.tab'
const TAB_USER = 'adissoo.tabUser'
const LAST_USER = 'adissoo.lastUser'
const sessionKey = (uid: string) => `adissoo.session.${uid}`

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

/** Sekmenin kalıcı kimliği; sekme çoğaltılınca kopyası da onu taşıyor. */
function tabId(): string {
  return safe(() => {
    let id = sessionStorage.getItem(TAB_ID)
    if (!id) {
      id = crypto.randomUUID().slice(0, 8)
      sessionStorage.setItem(TAB_ID, id)
    }
    return id
  }, 'default')
}

/** Bu sekmenin auth-js anahtarı. Yalnızca adı sekmeye özgü; içeriği değil. */
export function tabStorageKey(projectRef: string): string {
  return `sb-${projectRef}-auth-token-${tabId()}`
}

function uidOf(value: string): string | null {
  return safe(() => {
    const parsed = JSON.parse(value) as { user?: { id?: string } }
    return parsed.user?.id ?? null
  }, null)
}

/**
 * auth-js'in beklediği depolama arabirimi.
 *
 * `legacyKey`: bu düzenden önce oturumun durduğu tek anahtar. Açık oturumu
 * olanlar güncellemeden sonra çıkış yapmış bulunmasın diye bir kez okunup
 * yeni yerine taşınıyor.
 */
export function tabSessionStorage(mainKey: string, legacyKey: string) {
  /**
   * Bu sekmenin hesabı. Boş dize "bu sekmede çıkış yapıldı" demek: o zaman
   * son girilen hesaba düşülmüyor, yoksa garson sekmesinden çıkan kişi,
   * öteki sekmedeki patronun oturumunda buluverirdi kendini.
   */
  const currentUser = () =>
    safe(() => {
      const mine = sessionStorage.getItem(TAB_USER)
      if (mine !== null) return mine || null
      return localStorage.getItem(LAST_USER)
    }, null)

  const write = (value: string) => {
    const uid = uidOf(value)
    if (!uid) return
    safe(() => {
      localStorage.setItem(sessionKey(uid), value)
      localStorage.setItem(LAST_USER, uid)
      sessionStorage.setItem(TAB_USER, uid)
    }, undefined)
  }

  return {
    getItem(key: string): string | null {
      // Oturum dışındaki anahtarlar (PKCE doğrulayıcısı gibi) olduğu gibi.
      if (key !== mainKey) return safe(() => localStorage.getItem(key), null)

      const uid = currentUser()
      const found = uid ? safe(() => localStorage.getItem(sessionKey(uid)), null) : null
      if (found) return found
      if (safe(() => sessionStorage.getItem(TAB_USER) === '', false)) return null

      // Eski düzenden taşıma: bir kez, sonra eski anahtar siliniyor.
      const legacy = safe(() => localStorage.getItem(legacyKey), null)
      if (legacy) {
        write(legacy)
        safe(() => localStorage.removeItem(legacyKey), undefined)
        return legacy
      }
      return null
    },

    setItem(key: string, value: string): void {
      if (key !== mainKey) {
        safe(() => localStorage.setItem(key, value), undefined)
        return
      }
      write(value)
    },

    removeItem(key: string): void {
      if (key !== mainKey) {
        safe(() => localStorage.removeItem(key), undefined)
        return
      }
      // Çıkış: bu sekmenin hesabı. Aynı hesabın öteki sekmeleri de onunla
      // çıkıyor, tek kopyayı paylaşıyorlar. Başka hesapların sekmelerine
      // dokunulmuyor.
      const uid = currentUser()
      safe(() => {
        if (uid) {
          localStorage.removeItem(sessionKey(uid))
          if (localStorage.getItem(LAST_USER) === uid) localStorage.removeItem(LAST_USER)
        }
        sessionStorage.setItem(TAB_USER, '')
      }, undefined)
    },
  }
}
